import { Node } from "@tiptap/core";

/**
 * Tiptap node extension that preserves block-level HTML elements the editor
 * doesn't natively support (e.g. <table>, <div>, <details>, <dl>).
 *
 * Without this, Tiptap strips unsupported elements entirely. This node
 * captures them, stores the raw HTML, and renders a non-editable preview
 * in the editor while outputting the original HTML on serialize.
 */

const SUPPORTED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "s",
  "del",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "hr",
  "img",
  "span",
]);

const BLOCK_TAGS = new Set([
  "table",
  "div",
  "section",
  "details",
  "summary",
  "dl",
  "dt",
  "dd",
  "figure",
  "figcaption",
  "aside",
  "nav",
  "header",
  "footer",
  "main",
  "article",
  "address",
]);

export const RawHtmlBlock = Node.create({
  name: "rawHtmlBlock",
  group: "block",
  atom: true,
  draggable: true,

  // Lower priority than CustomComponent so web components match first
  priority: 40,

  addAttributes() {
    return {
      rawHtml: {
        default: "",
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "*",
        getAttrs(node) {
          if (!(node instanceof HTMLElement)) return false;

          const tag = node.tagName.toLowerCase();

          // Skip tags Tiptap handles natively
          if (SUPPORTED_TAGS.has(tag)) return false;

          // Skip web components (handled by CustomComponent)
          if (tag.includes("-")) return false;

          // Only capture known block-level elements
          if (!BLOCK_TAGS.has(tag)) return false;

          return {
            rawHtml: node.outerHTML,
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    // Parse the stored HTML and return the real DOM element so Tiptap
    // serializes the original markup in getHTML()
    const wrapper = document.createElement("div");
    wrapper.innerHTML = node.attrs.rawHtml;
    const el = wrapper.firstElementChild;

    if (el) {
      return { dom: el };
    }

    // Fallback: render as a div with the raw HTML inside
    const fallback = document.createElement("div");
    fallback.innerHTML = node.attrs.rawHtml;
    return { dom: fallback };
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("div");
      dom.className = "tiptap-raw-html-block";
      dom.contentEditable = "false";

      // Render a live preview of the HTML
      const preview = document.createElement("div");
      preview.className = "tiptap-raw-html-block-preview";
      preview.innerHTML = node.attrs.rawHtml;
      dom.appendChild(preview);

      return { dom };
    };
  },
});
